import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';
import { getLocalItem, getSessionItem } from '../../utils/storage';

import {
  StickyMobileCTA,
  SectionWrapper,
  PricingGrid,
  WhyUsGrid,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  RelatedLinks,
  StructuredData,
  BrandTrustBand,
  RelatedTopServices,
} from '../elements/premium';

import brandsData from '../../data/brands_data.json';
import brandKeywordMap from '../../data/brand_keyword_map.json';
import keywordPages from '../../data/keyword_pages.json';
import { startingPriceLabel, getBikeServiceCards, getCarServiceCards } from '../../utils/pricing';

const WHY_US_ITEMS = [
  { icon: '🏠', title: 'Doorstep Service', desc: 'Expert mechanics come to your home or office — no garage trips needed.' },
  { icon: '🎓', title: 'Certified Mechanics', desc: 'Trained, verified professionals with years of experience across all brands.' },
  { icon: '💰', title: 'Transparent Pricing', desc: 'No hidden charges. See exact pricing before you book.' },
  { icon: '🔒', title: 'Genuine Parts', desc: 'OEM and genuine spare parts only. Never compromise on quality.' },
  { icon: '📸', title: 'Live Updates', desc: 'Before/after photos, real-time progress tracking via our app.' },
  { icon: '🛡️', title: '30-Day Warranty', desc: 'Every service backed by a 30-day warranty on parts and labour.' },
];

// Merge entries to find brand + model
function findBrandAndModel(brandSlug, modelSlug) {
  const allBrands = [...(brandsData.bike || []), ...(brandsData.car || [])];
  const brandMatches = allBrands.filter((b) => b.slug === brandSlug);
  if (brandMatches.length === 0) return { brand: null, model: null };

  // For shared brands, merge models
  let brand = brandMatches[0];
  if (brandMatches.length > 1) {
    brand = {
      ...brandMatches[0],
      vehicleType: 'both',
      models: [...brandMatches[0].models, ...brandMatches[1].models]
        .filter((m, i, arr) => arr.findIndex((x) => x.slug === m.slug) === i),
      logo: brandMatches[0].logo || brandMatches[1].logo,
    };
  }

  const model = brand.models.find((m) => m.slug === modelSlug);
  return { brand, model };
}

class ModelPage extends Component {
  componentDidMount() {
    const { setOpenModal, isMobileApp, history } = this.props;
    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }
    if (isMobileApp) {
      history.push('/register');
    }
  }

  tryRequireLogo(logoFile) {
    if (!logoFile) return null;
    try {
      return require(`../../images/brand_logos/${logoFile}`);
    } catch (e) {
      return null;
    }
  }

  tryRequireImage(imagePath) {
    if (!imagePath) return null;
    try {
      return require(`../../images/vehicle_images/${imagePath}`);
    } catch (e) {
      return null;
    }
  }

  render() {
    const { brandSlug, modelSlug, isMobileBrowser, isMobileApp, user, setUser, setCitiesModal } = this.props;
    const { brand, model } = findBrandAndModel(brandSlug, modelSlug);

    if (!brand || !model) return <Redirect to="/error-404" />;

    const logo = this.tryRequireLogo(brand.logo);
    const modelImg = this.tryRequireImage(model.imagePath);
    const canonical = `https://www.ridenrepair.com/brands/${brandSlug}/${modelSlug}`;

    const isBike = brand.vehicleType === 'bike';
    const startPrice = startingPriceLabel(isBike ? 'bike' : 'car');
    const vehicleWord = isBike ? 'bike' : 'car';

    // Other models from same brand
    const otherModels = brand.models.filter((m) => m.slug !== modelSlug).slice(0, 8);

    // Related keywords
    const kwSlugs = brandKeywordMap[brandSlug] || [];
    const relatedKwLinks = kwSlugs
      .filter((s) => keywordPages[s])
      .slice(0, 6)
      .map((s) => ({
        title: keywordPages[s],
        href: `/${s}`,
        description: `Doorstep ${keywordPages[s].toLowerCase()} service with 30-day warranty.`,
      }));

    // Model-specific FAQs
    const faqs = [
      { q: `How much does ${model.label} service cost?`, a: `${model.label} service starts from ${startPrice}. Exact pricing depends on the service type. Prices are transparent — you see the cost before booking.` },
      { q: `Do you service ${model.label} at my doorstep?`, a: `Yes! Ride N Repair provides doorstep ${model.label} service across 32+ cities in India. Our certified mechanic arrives at your home or office.` },
      { q: `What services do you offer for ${model.label}?`, a: `We offer general service, engine repair, brake repair, ${isBike ? 'clutch repair, chain service' : 'AC repair, battery replacement'}, tyre service, and more for ${model.label}.` },
      { q: `Do you use genuine parts for ${model.label}?`, a: `We use OEM and genuine ${brand.label} spare parts for all repairs. Every part comes with a warranty.` },
      { q: `What warranty do you offer on ${model.label} service?`, a: `Every ${model.label} service includes a 30-day warranty on parts and labour. If any issue arises within 30 days, we fix it free.` },
    ];

    // Pricing — sourced from pricing-snapshot (backend SoT). Never hardcode prices.
    const packages = isBike ? getBikeServiceCards() : getCarServiceCards();

    // Schemas
    const schemas = [
      {
        '@type': 'Product',
        name: `${model.label} Service`,
        brand: { '@type': 'Brand', name: brand.label },
        description: `Professional doorstep ${model.label} ${vehicleWord} service and repair by Ride N Repair.`,
        offers: {
          '@type': 'Offer',
          price: startPrice.replace('₹', '').replace(',', ''),
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];

    const logoSrc = logo ? (typeof logo === 'object' ? logo.default : logo) : null;

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{`${model.label} Service & Repair — Doorstep Service | Ride N Repair`}</title>
          <meta name="description" content={`Book doorstep ${model.label} ${vehicleWord} service. Starting ${startPrice}. Certified mechanics, genuine ${brand.label} parts, 30-day warranty. 32+ cities.`} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={`${model.label} Service & Repair | Ride N Repair`} />
          <meta property="og:description" content={`Doorstep ${model.label} service starting ${startPrice}. Certified mechanics, genuine parts.`} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} transparent={false} />

        {/* MODEL HERO */}
        <section className="rr-model-hero">
          <div className="rr-model-hero__image">
            {modelImg ? (
              <img
                src={typeof modelImg === 'object' ? modelImg.default : modelImg}
                alt={model.label}
              />
            ) : (
              <div className="rr-model-hero__placeholder">{isBike ? '🏍️' : '🚗'}</div>
            )}
          </div>
          <div className="rr-model-hero__content">
            <Link to={`/brands/${brandSlug}`} className="rr-model-hero__badge">
              {logoSrc && <img src={logoSrc} alt={brand.label} />}
              {brand.label}
            </Link>
            <h1 className="rr-model-hero__h1">{model.label} Service & Repair</h1>
            <div className="rr-model-hero__price">Starting {startPrice}</div>
            <p className="rr-model-hero__sub">
              Professional doorstep service for your {model.label}. Certified mechanics, genuine {brand.label} parts, 30-day warranty.
            </p>
            <Link to="/service" className="rr-cta-primary">Book {model.label} Service</Link>
          </div>
        </section>

        <BrandTrustBand />

        <Breadcrumbs items={[
          { label: 'Home', href: '/' },
          { label: 'Brands', href: '/brands' },
          { label: brand.label, href: `/brands/${brandSlug}` },
          { label: model.label },
        ]} />

        <StickyMobileCTA title={model.label} priceHint={`Starting ${startPrice} · 30-Day Warranty`} btnLabel="Book Now" btnHref="/service" />

        {/* SERVICES / PRICING */}
        <SectionWrapper variant="dark" kicker="Transparent Pricing" heading={`${model.label} Service Pricing`}>
          <PricingGrid packages={packages} />
          <div className="rr-text-center rr-mt-32">
            <Link to="/service" className="rr-cta-primary">Book {model.label} Service Now</Link>
          </div>
        </SectionWrapper>

        {/* WHY US */}
        <SectionWrapper variant="gray" kicker="Our Promise" heading="Why 2,00,000+ Customers Choose Ride N Repair">
          <WhyUsGrid items={WHY_US_ITEMS} />
        </SectionWrapper>

        {/* OTHER MODELS */}
        {otherModels.length > 0 && (
          <SectionWrapper kicker={`More ${brand.label}`} heading={`Other ${brand.label} Models`}>
            <div className="rr-model-grid">
              {otherModels.map((m) => {
                const mImg = this.tryRequireImage(m.imagePath);
                return (
                  <Link key={m.slug} to={`/brands/${brandSlug}/${m.slug}`} className="rr-model-card">
                    {mImg ? (
                      <img
                        src={typeof mImg === 'object' ? mImg.default : mImg}
                        alt={m.label}
                        className="rr-model-card__img"
                        loading="lazy"
                      />
                    ) : (
                      <div className="rr-model-card__placeholder">{isBike ? '🏍️' : '🚗'}</div>
                    )}
                    <span className="rr-model-card__name">{m.label}</span>
                    <span className="rr-model-card__cta">View Services &rarr;</span>
                  </Link>
                );
              })}
            </div>
          </SectionWrapper>
        )}

        {/* RELATED KEYWORDS */}
        {relatedKwLinks.length > 0 && (
          <SectionWrapper variant="gray" kicker="Related Services" heading="Related Doorstep Services">
            <RelatedLinks links={relatedKwLinks} />
          </SectionWrapper>
        )}

        {/* FAQ */}
        <SectionWrapper narrow kicker="FAQ" heading={`${model.label} Service — Common Questions`}>
          <FaqAccordion faqs={faqs} withSchema={false} />
        </SectionWrapper>

        <RelatedTopServices variant="generic" kicker="Popular Doorstep Services" heading="Customers Also Book" />

        <FinalCTA
          heading={<>Need {model.label} Service?<br />Book Now — Get It Done Today.</>}
          subtitle={`Doorstep service · Certified mechanics · ${startPrice} onwards · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default ModelPage;
